/* eslint-disable no-useless-computed-key */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useStopwatch } from 'react-timer-hook';
import style from './CheckboxState.style';
import { DocumentStatus, ScorecardStatus } from '@Constants/status';

// Checkbox statuses
const CheckboxStateSvg = ({
    className,
    labels,
    documentStatus,
    scorecardStatus,
    start,
    setTooMuchTimeInitializingAI,
    setTooMuchTimeAnalyzingDoc,
    setTooMuchTimeGeneratingScorecards,
    setInitializationTimeout,
    setAnalyzingDocTimeout,
    setScorecardsTimeout
}) => {
    const [fadeIn] = useState('fade-in');
    const [statusActive] = useState('status-active');

    const [fadeStates, setFadeStates] = useState({});
    const [activeStates, setActiveStates] = useState(
        Object.assign(...labels.map(label => ({ [label]: false })))
    ); // return an object {"state1": false, "state2": false, ...}
    const [checkboxs, setCheckbox] = useState(
        Object.assign(...labels.map(label => ({ [label]: false })))
    );

    const {
        start: startAIInitializationTime,
        pause: pauseAIInitializationTime,
        reset: resetAIInitializationTime,
        minutes: AIInitializationMinutes,
        seconds: AIInitializationSeconds,
        isRunning: AIInitializationTimeIsRunning
    } = useStopwatch();
    const {
        start: startDocumentAnalysisTime,
        pause: pauseDocumentAnalysisTime,
        reset: resetDocumentAnalysisTime,
        minutes: documentAnalysisMinutes,
        seconds: documentAnalysisSeconds,
        isRunning: documentAnalysisTimeIsRunning
    } = useStopwatch();
    const {
        start: startScorecardGenerationTime,
        pause: pauseScorecardGenerationTime,
        reset: resetScorecardGenerationTime,
        minutes: scorecardGenerationMinutes,
        seconds: scorecardGenerationSeconds,
        isRunning: scorecardGenerationTimeIsRunning
    } = useStopwatch();

    useEffect(
        () => {
            // Fade-In states
            if (start) {
                labels.forEach((label, idx) => {
                    setTimeout(() => {
                        setFadeStates(prev => ({ ...prev, [label]: true }));
                    }, idx * 500);
                });
            } else {
                labels.forEach(label => {
                    // clear all when stop
                    setFadeStates(prev => ({ ...prev, [label]: false }));
                    setCheckbox(prev => ({ ...prev, [label]: false }));
                    setActiveStates(prev => ({
                        ...prev,
                        [label]: false
                    }));
                });
            }
        },
        [start]
    );

    useEffect(
        () => {
            // Active-States
            if (start) {
                if (documentStatus === DocumentStatus.INITIALIZING) {
                    // Initializing
                    setActiveStates(prev => ({ ...prev, ['Initializing']: true }));
                }

                if (documentStatus === DocumentStatus.PENDING) {
                    setActiveStates(prev => ({ ...prev, ['Initializing']: false }));
                    setCheckbox(prev => ({ ...prev, ['Initializing']: true }));

                    // Analyzing document
                    setActiveStates(prev => ({ ...prev, ['Analyzing document']: true }));
                }

                if (documentStatus === DocumentStatus.PROCESSED) {
                    setActiveStates(prev => ({ ...prev, ['Analyzing document']: false }));
                    setCheckbox(prev => ({ ...prev, ['Analyzing document']: true }));
                }
            }
        },
        [documentStatus]
    );

    useEffect(
        () => {
            // Active-States
            if (start) {
                if (scorecardStatus === ScorecardStatus.PENDING) {
                    // Generating scorecard
                    setActiveStates(prev => ({ ...prev, ['Generating scorecard']: true }));
                }
                if (scorecardStatus === ScorecardStatus.PROCESSED) {
                    setActiveStates(prev => ({ ...prev, ['Generating scorecard']: false }));
                    setCheckbox(prev => ({ ...prev, ['Generating scorecard']: true }));
                }
            }
        },
        [scorecardStatus]
    );

    /** record the time taken by each stage of document processing to be used
     * in the parent component to display warnings
     */
    useEffect(
        () => {
            if (start) {
                /** record AI initialization time */
                if (activeStates.Initializing && !AIInitializationTimeIsRunning) {
                    startAIInitializationTime();
                }
                if (!activeStates.Initializing && AIInitializationTimeIsRunning) {
                    pauseAIInitializationTime();
                    resetAIInitializationTime();
                    setTooMuchTimeInitializingAI(false);
                    setInitializationTimeout(false);
                }
                if (
                    AIInitializationSeconds > 15 &&
                    AIInitializationTimeIsRunning &&
                    activeStates.Initializing
                ) {
                    setTooMuchTimeInitializingAI(true);
                }
                if (
                    AIInitializationSeconds > 15 &&
                    AIInitializationMinutes > 0 &&
                    AIInitializationTimeIsRunning &&
                    activeStates.Initializing
                ) {
                    setInitializationTimeout(true);
                }

                /** record document analysis time */
                if (activeStates['Analyzing document'] && !documentAnalysisTimeIsRunning) {
                    startDocumentAnalysisTime();
                }
                if (!activeStates['Analyzing document'] && documentAnalysisTimeIsRunning) {
                    pauseDocumentAnalysisTime();
                    resetDocumentAnalysisTime();
                    setTooMuchTimeAnalyzingDoc(false);
                    setAnalyzingDocTimeout(false);
                }
                if (
                    documentAnalysisMinutes > 1 &&
                    documentAnalysisTimeIsRunning &&
                    activeStates['Analyzing document']
                ) {
                    setTooMuchTimeAnalyzingDoc(true);
                }
                if (
                    documentAnalysisMinutes > 2 &&
                    documentAnalysisSeconds > 30 &&
                    documentAnalysisTimeIsRunning &&
                    activeStates['Analyzing document']
                ) {
                    setAnalyzingDocTimeout(true);
                }

                /** record scorecard generation time */
                if (activeStates['Generating scorecard'] && !scorecardGenerationTimeIsRunning) {
                    startScorecardGenerationTime();
                }
                if (!activeStates['Generating scorecard'] && scorecardGenerationTimeIsRunning) {
                    pauseScorecardGenerationTime();
                    resetScorecardGenerationTime();
                    setTooMuchTimeGeneratingScorecards(false);
                    setScorecardsTimeout(false);
                }
                if (
                    scorecardGenerationSeconds > 20 &&
                    scorecardGenerationTimeIsRunning &&
                    activeStates['Generating scorecard']
                ) {
                    setTooMuchTimeGeneratingScorecards(true);
                }
                if (
                    scorecardGenerationSeconds > 20 &&
                    scorecardGenerationMinutes > 0 &&
                    scorecardGenerationTimeIsRunning &&
                    activeStates['Generating scorecard']
                ) {
                    setScorecardsTimeout(true);
                }
            }
        },
        [
            activeStates,
            AIInitializationMinutes,
            AIInitializationSeconds,
            documentAnalysisMinutes,
            documentAnalysisSeconds,
            scorecardGenerationMinutes,
            scorecardGenerationSeconds
        ]
    );
    return (
        <div className={className}>
            <svg viewBox="0 0 0 0" className="inline-checkbox-svg">
                <defs>
                    <linearGradient
                        id="boxGradient"
                        gradientUnits="userSpaceOnUse"
                        x1="0"
                        y1="0"
                        x2="25"
                        y2="25"
                    >
                        <stop offset="0%" stopColor="#bbb1a5" />
                        <stop offset="100%" stopColor="#bbb1a5" />
                    </linearGradient>
                    <linearGradient id="lineGradient">
                        <stop offset="0%" stopColor="#bbb1a5" />
                        <stop offset="100%" stopColor="#e8e1d9" />
                    </linearGradient>
                    <path id="status-check" stroke="url(#boxGradient)" d="M10 13l2 2 5-5" />
                    <circle id="status-circle" cx="13.5" cy="12.5" r="10" />
                </defs>
            </svg>

            <div className="status-list">
                {labels.map((label, idx) => (
                    <span
                        key={idx}
                        className={`status ${fadeStates[label] && fadeIn} ${activeStates[label] &&
                            statusActive}`}
                    >
                        <input
                            type="checkbox"
                            className="status-state"
                            checked={checkboxs[label]}
                            readOnly
                            data-testid={`${label}_animation_check`}
                        />
                        <svg viewBox="0 0 200 25" className="status-icon">
                            <use href="#status-box" className="status-box" />
                            <use href="#status-check" className="status-check" />
                            <use href="#status-circle" className="status-circle" />
                        </svg>
                        <div className="status-text" data-testid={`${label}_animation_label`}>
                            {label}
                        </div>
                    </span>
                ))}
            </div>
        </div>
    );
};

export const CheckboxState = styled(CheckboxStateSvg)`
    ${style};
`;
